import React, {useState, useEffect, useMemo} from 'react';
import {Box} from '@mui/material';
import {Icon} from '@lightricks/react-design-system';
import ToggleButton from '@contentlab/components/toggle-button';
import {DEFAULT_DEBOUNCE_DELAY} from '@contentlab/config/constants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import contentLabStore, {
  DraftCaptionsProject,
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {CaptionsStylePreset} from '@contentlab/types/captions';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {useDebouncedCallback} from '@/hooks/use-debounce';
import {CaptionsTab} from '../../types';
import CaptionsEditor from '../captions-editor/CaptionsEditor';
import LanguagesContent from '../languages-content/LanguagesContent';
import StyleSelector from '../style-selector';

interface CaptionsMenuProps {
  projectId: string;
  draftCaptionsProject: DraftCaptionsProject;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
  captionStyles: CaptionsStylePreset[];
  onStyleChange?: () => void;
  onTranscriptionChange?: () => void;
  onCustomizeStyle?: (style: CaptionsStylePreset) => void;
  activeTab: CaptionsTab;
  setActiveTab: (tab: CaptionsTab) => void;
}

function CaptionsMenu({
  projectId,
  draftCaptionsProject,
  currentIndex,
  setCurrentIndex,
  captionStyles,
  onStyleChange,
  onTranscriptionChange,
  onCustomizeStyle,
  activeTab,
  setActiveTab,
}: CaptionsMenuProps) {
  const [selectedStyle, setSelectedStyle] =
    useState<CaptionsStylePreset | null>(
      draftCaptionsProject.selectedStyle || null
    );

  const {uiInteraction} = useUiInteractionEvent();
  const editedStyleIds = contentLabActions.getEditedStyleIds();
  const {originalCaptionStyles} = contentLabStore.getState();

  const getActuallyEditedStyleIds = useMemo(() => {
    const actuallyEditedIds = new Set<string>();

    editedStyleIds.forEach((styleId) => {
      const editedStyle = contentLabActions.getEditedStyle(styleId);
      const originalStyle = originalCaptionStyles.find((s) => s.id === styleId);

      if (
        editedStyle &&
        originalStyle &&
        JSON.stringify(editedStyle.captionsStyle) !==
          JSON.stringify(originalStyle.captionsStyle)
      ) {
        actuallyEditedIds.add(styleId);
      }
    });

    return actuallyEditedIds;
  }, [editedStyleIds, originalCaptionStyles]);

  useEffect(() => {
    if (captionStyles.length > 0 && originalCaptionStyles.length === 0) {
      contentLabActions.setOriginalCaptionStyles([...captionStyles]);
    }
  }, [captionStyles, originalCaptionStyles]);

  // Sync local selectedStyle with global state
  useEffect(() => {
    if (draftCaptionsProject.selectedStyle && !selectedStyle) {
      setSelectedStyle(draftCaptionsProject.selectedStyle);
    }
  }, [draftCaptionsProject.selectedStyle, selectedStyle]);

  const handleStyleSelect = (style: CaptionsStylePreset) => {
    const editedStyle = contentLabActions.getEditedStyle(style.id);
    const styleToUse = editedStyle || style;

    uiInteraction({
      item_name: 'captions_style_selected',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_STYLES,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
      interaction_details: JSON.stringify({
        style_id: style.id,
        style_name: style.name,
        is_edited: !!editedStyle,
        previous_style_id: selectedStyle?.id || null,
      }),
    });

    setSelectedStyle(styleToUse);
    contentLabActions.setDraftCaptionsProject({
      ...draftCaptionsProject,
      selectedStyle: styleToUse,
    });
    onStyleChange?.();
  };

  const handleResetStyle = (style: CaptionsStylePreset) => {
    const originalStyle = originalCaptionStyles.find((s) => s.id === style.id);
    if (originalStyle) {
      uiInteraction({
        item_name: 'captions_style_reset',
        interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_STYLES,
        flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
        interaction_details: JSON.stringify({
          style_id: style.id,
          style_name: style.name,
          was_currently_selected: selectedStyle?.id === style.id,
        }),
      });

      contentLabActions.resetStyleToOriginal(style.id);

      if (selectedStyle?.id === style.id) {
        setSelectedStyle(originalStyle);
        contentLabActions.setDraftCaptionsProject({
          ...draftCaptionsProject,
          selectedStyle: originalStyle,
        });
      }

      onStyleChange?.();
    }
  };

  const debouncedTranscriptionUpdate = useDebouncedCallback(async () => {
    onTranscriptionChange?.();
  }, DEFAULT_DEBOUNCE_DELAY);

  const editorContent = (
    <CaptionsEditor
      draftCaptionsProject={draftCaptionsProject}
      currentIndex={currentIndex}
      setCurrentIndex={setCurrentIndex}
      onTranscriptionChange={debouncedTranscriptionUpdate}
    />
  );

  const stylesContent = (
    <StyleSelector
      styles={captionStyles}
      onStyleSelect={handleStyleSelect}
      onCustomize={onCustomizeStyle || (() => {})}
      onReset={handleResetStyle}
      selectedStyle={selectedStyle}
      editedStyles={getActuallyEditedStyleIds}
    />
  );

  const languagesContent = (
    <LanguagesContent draftCaptionsProject={draftCaptionsProject} />
  );

  const renderTabContent = (tab: CaptionsTab) => {
    switch (tab) {
      case 'styles':
        return stylesContent;
      case 'editor':
        return editorContent;
      case 'languages':
        return languagesContent;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingRight: '24px',
        paddingTop: '24px',
        boxSizing: 'border-box',
      }}
    >
      <ToggleButton
        options={[
          {
            label: translate('Style'),
            value: 'styles',
            icon: (
              <Icon
                name="Features-Glitter"
                size="medium"
                appearance="primary"
              />
            ),
          },
          {
            label: translate('Edit Text'),
            value: 'editor',
            icon: (
              <Icon name="Features-Text" size="medium" appearance="primary" />
            ),
          },
          {
            label: translate('Translate'),
            value: 'languages',
            icon: (
              <Icon
                name="Navigation-Language"
                size="medium"
                appearance="primary"
              />
            ),
          },
        ]}
        value={activeTab}
        onChange={(tab) => {
          uiInteraction({
            item_name: `captions_tab_${tab}`,
            interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_TABS,
            flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
            interaction_details: JSON.stringify({
              from_tab: activeTab,
              to_tab: tab,
            }),
          });
          setActiveTab(tab as CaptionsTab);
        }}
        showBorder={false}
        disableSlideAnimation
        layout="equal"
        gap="2px"
        padding="0"
        borderRadius="0"
      />

      <Box
        role="tabpanel"
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          marginTop: '16px',
          marginX: '-24px',
        }}
      >
        {renderTabContent(activeTab)}
      </Box>
    </Box>
  );
}

export default CaptionsMenu;
